import { StepProps } from 'components/Wizards/MultiStepWizard/Types'
import { GrabHandle } from 'blaze-ui/icons'
import Styles from './SectionsSelection.module.scss'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useUnsplashImages } from 'components/Wizards/SelectImages/ImagesProvider'
import { useSections } from './SectionsProvider'
import { LoadingSections } from './LoadingSections'
import { Section } from './Section'
import { Field } from 'components/Wizards/Field/Field'
import { useEffect, useState } from 'react'
import { useField, useFormikContext } from 'formik'
import cx from 'classnames'
import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { NewsletterSection } from './Types'

export const SortableItem = ({ id, section, index }: { id: string; section: NewsletterSection; index: number }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const { isMobile } = useResponsiveDesign()

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div ref={setNodeRef} style={style} className={cx(Styles.section, { [Styles.isDragging]: isDragging })}>
      {!isMobile && (
        <div className={Styles.dragHandle} {...attributes} {...listeners} role="button" data-testid='grab-handle'>
          <GrabHandle size={20} />
        </div>
      )}
      <Section section={section} index={index} />
    </div>
  )
}

export const SectionsField = (
  props: StepProps & {
    isGeneratingMore: boolean
  },
) => {
  const { schema, isGeneratingMore } = props
  const { isMobile } = useResponsiveDesign()
  const { sections, isLoading: isLoadingSections, updateSections } = useSections()
  const { isLoading: isLoadingImages } = useUnsplashImages()
  const { isSubmitting } = useFormikContext()

  const [, , { setValue: setSectionImages }] = useField<string[]>('section_images')
  const [, , { setValue: setSectionTitles }] = useField<string[]>('section_titles')
  const sectionTitlesField = Object.entries(schema.fields).find(([name, _]) => name === 'section_titles')
  const sectionTitlesMeta = sectionTitlesField?.[1].describe().meta

  const [localSections, setLocalSections] = useState<string[]>([])

  useEffect(() => {
    setLocalSections(sections.map((sections) => sections.id))
  }, [sections])

  // Function to handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    // If dropped outside the list or at the same position, do nothing
    if (!over || active.id === over.id) return

    // Get indices of the dragged and the moved items
    const grabbedIndex = localSections.indexOf(active.id as string)
    const movedIndex = localSections.indexOf(over.id as string)

    setLocalSections(arrayMove(localSections, grabbedIndex, movedIndex))

    // Update the section in the provider
    const newSections = arrayMove(sections, grabbedIndex, movedIndex)
    updateSections(newSections)
  }

  const isLoading = isLoadingImages || isLoadingSections

  useEffect(() => {
    setSectionTitles(sections.map((section) => section.title))
    setSectionImages(sections.map((section) => section.imageUrl || ''))
  }, [sections]) // eslint-disable-line

  return (
    <>
      {sectionTitlesField && (
        <Field
          key={sectionTitlesField[0]}
          label={isMobile ? undefined : sectionTitlesMeta.label}
          description={isMobile ? undefined : sectionTitlesMeta.description}
        >
          <>
            {isLoading && !isGeneratingMore ? (
              <div className={Styles.sectionsContainer}>
                <LoadingSections />
              </div>
            ) : (
              <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={localSections} disabled={isSubmitting}>
                  <div className={Styles.sectionsContainer}>
                    {localSections.map((sectionId, index) => {
                      const section = sections.find((section) => section.id === sectionId)
                      if (!section) return null

                      return <SortableItem index={index} key={section.id} section={section} id={section.id} />
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            )}
            {isLoading && isGeneratingMore && (
              <div className={cx(Styles.sectionsContainer, Styles.loadingMore)}>
                <LoadingSections initialPosition={sections.length} />
              </div>
            )}
          </>
        </Field>
      )}
    </>
  )
}
